import { createApi, fetchBaseQuery, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { getApiConfig } from '@/config/api';
import { getRequestLanguage } from '@/utils/requestLanguage';
import type {
  ApiResponse,
  Subscription,
  SubscriptionPayment,
  SubscriptionPaymentHistoryParams,
} from '@/types';

interface PaymentHistoryResponse {
  data: SubscriptionPayment[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Create a dynamic base query for subscription API
const dynamicSubscriptionBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as any).auth.token;
      if (!token) {
        token = localStorage.getItem('token');
      }
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('ngrok-skip-browser-warning', 'true');
      headers.set('accept-language', getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const subscriptionApi = createApi({
  reducerPath: 'subscriptionApi',
  baseQuery: dynamicSubscriptionBaseQuery,
  tagTypes: ['Subscription', 'SubscriptionPayment'],
  endpoints: (builder) => ({
    // Get subscription info for a store
    getSubscription: builder.query<ApiResponse<Subscription>, string>({
      query: (storeId) => `/stores/${storeId}/subscription`,
      providesTags: (_, __, storeId) => [{ type: 'Subscription', id: storeId }],
    }),

    // Get payment history for a subscription
    getPaymentHistory: builder.query<
      PaymentHistoryResponse,
      { storeId: string; params?: SubscriptionPaymentHistoryParams }
    >({
      query: ({ storeId, params }) => ({
        url: `/stores/${storeId}/subscription/payments`,
        params,
      }),
      providesTags: (_, __, { storeId }) => [{ type: 'SubscriptionPayment', id: storeId }],
    }),

    // Get payment detail
    getPaymentDetail: builder.query<
      ApiResponse<SubscriptionPayment>,
      { storeId: string; paymentId: string }
    >({
      query: ({ storeId, paymentId }) => `/stores/${storeId}/subscription/payments/${paymentId}`,
      providesTags: (_, __, { paymentId }) => [{ type: 'SubscriptionPayment', id: paymentId }],
    }),
  }),
});

export const {
  useGetSubscriptionQuery,
  useGetPaymentHistoryQuery,
  useGetPaymentDetailQuery,
} = subscriptionApi;
